<template>
  <div id="SectionPlanCompare">
    <UIContainer class="SectionPlanCompare__container">
      <div class="SectionPlanCompare__wrapper">
        <table class="SectionPlanCompare__table">
          <!-- Table Header -->
          <thead>
            <tr class="SectionPlanCompare__header-row">
              <th
                class="SectionPlanCompare__header-cell SectionPlanCompare__header-cell--feature"
              >
                <!-- Empty cell for features column -->
              </th>
              <th
                v-for="plan in plans"
                :key="plan.id"
                :class="[
                  'SectionPlanCompare__header-cell',
                  'SectionPlanCompare__header-cell--plan',
                  {
                    'SectionPlanCompare__header-cell--featured':
                      plan.isFeatured,
                  },
                ]"
              >
                <div class="SectionPlanCompare__plan-header">
                  <h3 class="SectionPlanCompare__plan-title">
                    {{ plan.title }}
                  </h3>
                  <p class="SectionPlanCompare__plan-description">
                    {{ plan.description }}
                  </p>
                  <UIButton
                    size="sm"
                    :to="{ name: 'Contact' }"
                    :theme="plan.ctaTheme"
                    :buttonStyle="plan.ctaStyle"
                    class="SectionPlanCompare__plan-cta"
                    isFull
                  >
                    {{ plan.ctaText }}
                  </UIButton>
                </div>
              </th>
            </tr>
          </thead>

          <!-- Table Body -->
          <tbody>
            <template v-for="category in featureCategories" :key="category.id">
              <!-- Category Header Row -->
              <tr class="SectionPlanCompare__category-row">
                <td class="SectionPlanCompare__category-cell">
                  <div class="SectionPlanCompare__category-title-wrapper">
                    <h4 class="SectionPlanCompare__category-title">
                      {{ category.title }}
                    </h4>
                    <UITooltip
                      v-if="category.tooltip"
                      class="SectionPlanCompare__category-tooltip"
                      :arrow="true"
                    >
                      <template #content>
                        {{ category.tooltip }}
                      </template>
                      <UIIcon name="help" />
                    </UITooltip>
                  </div>
                </td>
                <td
                  v-for="plan in plans"
                  :key="`${category.id}-${plan.id}`"
                  :class="[
                    'SectionPlanCompare__category-cell',
                    'SectionPlanCompare__category-cell--plan',
                    {
                      'SectionPlanCompare__category-cell--featured':
                        plan.isFeatured,
                    },
                  ]"
                >
                  <!-- Empty cells for plan columns -->
                </td>
              </tr>

              <!-- Feature Rows -->
              <tr
                v-for="feature in category.features"
                :key="feature.id"
                class="SectionPlanCompare__feature-row"
              >
                <td class="SectionPlanCompare__feature-name">
                  <span>{{ feature.name }}</span>
                  <UITooltip
                    v-if="feature.tooltip"
                    class="SectionPlanCompare__feature-tooltip"
                  >
                    <template #content>
                      {{ feature.tooltip }}
                    </template>
                    <UIIcon name="help" />
                  </UITooltip>
                </td>
                <td
                  v-for="plan in plans"
                  :key="`${feature.id}-${plan.id}`"
                  class="SectionPlanCompare__feature-value"
                >
                  <div
                    v-if="feature.values[plan.id] === true"
                    class="SectionPlanCompare__checkmark"
                  >
                    <UIIcon name="checkCircleOutline" />
                  </div>
                  <div
                    v-else-if="feature.values[plan.id] === false"
                    class="SectionPlanCompare__dash"
                  >
                    —
                  </div>
                  <div v-else class="SectionPlanCompare__value">
                    {{ feature.values[plan.id] }}
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </UIContainer>
  </div>
</template>

<script setup lang="ts">
import UIContainer from '@/views/components/ui/containers/UIContainer/UIContainer.vue'
import UIButton from '@/views/components/ui/buttons/UIButton.vue'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'
import UITooltip from '@/views/components/ui/tooltips/UITooltip.vue'

interface IPlan {
  id: string
  title: string
  description: string
  ctaText: string
  ctaTheme: 'primary' | 'secondary' | 'light'
  ctaStyle: 'filled' | 'outlined' | 'pale'
  isFeatured: boolean
  badge?: string
}

interface IFeature {
  id: string
  name: string
  tooltip?: string
  values: Record<string, boolean | string | number>
}

interface IFeatureCategory {
  id: string
  title: string
  tooltip?: string
  features: IFeature[]
}

const plans: IPlan[] = [
  {
    id: 'starter',
    title: 'GHG starter',
    description: 'Small company plan.',
    ctaText: 'Get in touch',
    ctaTheme: 'light',
    ctaStyle: 'outlined',
    isFeatured: false,
  },
  {
    id: 'core',
    title: 'Core plan',
    description: 'Best for growing companies.',
    ctaText: 'Get in touch',
    ctaTheme: 'primary',
    ctaStyle: 'filled',
    isFeatured: true,
  },
  {
    id: 'premium',
    title: 'Premium plan',
    description: 'Best for large enterprises.',
    ctaText: 'Get in touch',
    ctaTheme: 'light',
    ctaStyle: 'outlined',
    isFeatured: false,
  },
]

const featureCategories: IFeatureCategory[] = [
  {
    id: 'data-ingestion',
    title: 'Data ingestion & automation',
    features: [
      {
        id: 'connectors',
        name: 'Connectors: utilities, ERP/AP, HRIS, T&E, 3PLs',
        values: {
          starter: false,
          core: true,
          premium: true,
        },
      },
      {
        id: 'ai-doc-processing',
        name: 'AI doc processing for bills, waybills, RECs/GOs, EPDs',
        values: {
          starter: 10,
          core: 20,
          premium: 'Unlimited',
        },
      },
      {
        id: 'de-dupe',
        name: 'De-dupe',
        values: {
          starter: '20 GB',
          core: '40 GB',
          premium: 'Unlimited',
        },
      },
      {
        id: 'unit-validation',
        name: 'Unit/data/ID validation',
        values: {
          starter: false,
          core: true,
          premium: true,
        },
      },
    ],
  },
  {
    id: 'governance',
    title: 'Governance & controls',
    tooltip:
      'Governance & controls Prevent restatements and pass assurance with controlled factors and a one-click evidence trail.',
    features: [
      {
        id: 'factor-library',
        name: 'Factor library (EEA/eGRID/SmartWay/EPDs) with version control',
        values: {
          starter: 'Basic',
          core: 'Advanced',
          premium: 'Advanced',
        },
      },
      {
        id: 'approvals',
        name: 'Approvals',
        values: {
          starter: true,
          core: true,
          premium: true,
        },
      },
      {
        id: 'change-log',
        name: 'Change log',
        values: {
          starter: true,
          core: true,
          premium: true,
        },
      },
      {
        id: 'audit-trail',
        name: 'Audit trail',
        values: {
          starter: false,
          core: true,
          premium: true,
        },
      },
      {
        id: 'residual-mix',
        name: 'Residual mix automation',
        values: {
          starter: false,
          core: true,
          premium: true,
        },
      },
      {
        id: 'saved-reports',
        name: 'Saved reports',
        values: {
          starter: false,
          core: true,
          premium: true,
        },
      },
      {
        id: 'customer-properties',
        name: 'Customer properties',
        values: {
          starter: false,
          core: false,
          premium: true,
        },
      },
      {
        id: 'custom-fields',
        name: 'Custom fields',
        values: {
          starter: false,
          core: false,
          premium: true,
        },
      },
    ],
  },
  {
    id: 'scope3-suppliers',
    title: 'Scope 3 & suppliers',
    features: [
      {
        id: 'sso-saml',
        name: 'SSO/SAML authentication',
        values: {
          starter: true,
          core: true,
          premium: true,
        },
      },
      {
        id: 'advanced-permissions',
        name: 'Advanced permissions',
        values: {
          starter: false,
          core: true,
          premium: true,
        },
      },
      {
        id: 'audit-log',
        name: 'Audit log',
        values: {
          starter: false,
          core: false,
          premium: true,
        },
      },
      {
        id: 'data-history',
        name: 'Data history',
        values: {
          starter: false,
          core: false,
          premium: true,
        },
      },
    ],
  },
]
</script>

<style lang="scss" scoped>
#SectionPlanCompare {
  background-color: $color-white;
  padding: 4rem 0;

  .SectionPlanCompare__container {
    padding-top: 0;
    padding-bottom: 0;
  }

  .SectionPlanCompare__wrapper {
    background-color: $color-white;
    overflow: hidden;
  }

  .SectionPlanCompare__table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
  }

  // Header
  .SectionPlanCompare__header-row {
    background-color: $color-white;
  }

  .SectionPlanCompare__header-cell {
    padding: 1.5rem;
    text-align: center;
    vertical-align: top;

    &--feature {
      width: 40%;
    }

    &--plan {
      width: 20%;
    }

    &--featured {
      position: relative;
      border-left: 2px solid $color-ui-brand-500;
      border-right: 2px solid $color-ui-brand-500;
      border-top: 2px solid $color-ui-brand-500;
      border-top-left-radius: 1rem;
      border-top-right-radius: 1rem;
    }
  }

  .SectionPlanCompare__plan-header {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .SectionPlanCompare__plan-title {
    @include font-size-20px();
    font-weight: 600;
    color: $color-ui-gray-cool-900;
    margin: 0;
  }

  .SectionPlanCompare__plan-description {
    @include font-size-14px();
    color: $color-ui-gray-cool-600;
    margin: 0;
  }

  .SectionPlanCompare__plan-cta-link {
    text-decoration: none;
    display: block;
  }

  .SectionPlanCompare__plan-cta {
    margin-top: 0.5rem;
  }

  // Category Header
  .SectionPlanCompare__category-row {
    &:nth-child(even) {
      background-color: $color-ui-gray-cool-50;
    }
  }

  .SectionPlanCompare__category-cell {
    padding: 1rem 2rem;
    vertical-align: middle;

    &--plan {
      padding: 1rem;
    }

    &--featured {
      border-left: 2px solid $color-ui-brand-500;
      border-right: 2px solid $color-ui-brand-500;
    }
  }

  .SectionPlanCompare__category-title-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .SectionPlanCompare__category-title {
    @include font-size-16px();
    font-weight: 600;
    color: $color-ui-accent-main;
    margin: 0;
  }

  .SectionPlanCompare__category-tooltip {
    .UITooltip__icon {
      --size: 1rem;
      color: $color-ui-gray-cool-400;
    }
  }

  // Feature Rows
  .SectionPlanCompare__feature-row {
    &:nth-child(even) {
      background-color: $color-ui-gray-cool-50;
    }

    &:last-of-type {
      td:nth-child(3) {
        border-bottom: 2px solid $color-ui-brand-500;
        border-bottom-left-radius: 1rem;
        border-bottom-right-radius: 1rem;
      }
    }
  }

  .SectionPlanCompare__feature-name {
    @include font-size-14px();
    padding: 0.75rem 1.5rem;
    color: $color-ui-gray-cool-900;
    font-weight: 500;
    text-align: left;
    vertical-align: middle;

    span {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
    }
  }

  .SectionPlanCompare__feature-tooltip {
    display: inline-flex;
    margin-left: 0.25rem;

    .UITooltip__icon {
      --size: 0.875rem;
      color: $color-ui-gray-cool-400;
    }
  }

  .SectionPlanCompare__feature-value {
    padding: 1rem;
    text-align: center;
    vertical-align: middle;

    &:nth-child(3) {
      border-left: 2px solid $color-ui-brand-500;
      border-right: 2px solid $color-ui-brand-500;
    }
  }

  .SectionPlanCompare__checkmark {
    color: $color-ui-accent-main;
    display: inline-flex;

    .UIIcon {
      --size: 1.25rem;
    }
  }

  .SectionPlanCompare__dash {
    @include font-size-16px();
    color: $color-ui-gray-cool-400;
    font-weight: 400;
  }

  .SectionPlanCompare__value {
    @include font-size-14px();
    color: $color-ui-gray-cool-700;
    font-weight: 500;
  }

  // Responsive design
  @include md {
    // Maintain table layout
  }
}

// Custom tooltip theme
:deep(.tippy-box[data-theme~='dark']) {
  background: $color-ui-accent-main;
  color: $color-white;
  border: none;
  border-radius: 0.5rem;
  max-width: 20rem;
  @include font-size-14px();

  .tippy-arrow {
    color: $color-ui-accent-main;
  }
}
</style>
